import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getMenuItems } from '../services/api';
import { toast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import ReviewNudge from '../components/ReviewNudge';
import { trackEvent } from '../lib/ga';

// Returns a .webp srcSet ONLY for local /images/ repo assets (which ship with a
// .webp sibling). For uploaded/remote URLs (http...), returns null so we skip the
// <source> and let the original <img> render — uploaded files have no .webp twin.
const localWebpSrc = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (!url.startsWith('/images/')) return null;
  if (!/\.(jpg|png)$/i.test(url)) return null;
  return url.replace(/\.(jpg|png)$/i, '.webp');
};

const Home = () => {
  const [iceCreams, setIceCreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entryType, setEntryType] = useState('customer');
  const [winners, setWinners] = useState({ current_winner: null, past_winners: [] });
  const [pageContent, setPageContent] = useState(null);
  const [drawingForm, setDrawingForm] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    smsConsent: false,
    comment: '',
    // Vendor fields
    companyName: '',
    companyPhone: '',
    websiteUrl: '',
    position: '',
    bestContactMethod: 'email'
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch CMS page content
        try {
          const pageRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/home`);
          if (pageRes.ok) {
            const data = await pageRes.json();
            if (data) setPageContent(data);
          }
        } catch (e) {
          console.log('Using default home content');
        }

        const iceCreamData = await getMenuItems('ice_cream');
        setIceCreams(iceCreamData.slice(0, 6));
        
        // Fetch current winners for display
        try {
          const winnersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners`);
          if (winnersRes.ok) {
            setWinners(await winnersRes.json());
          }
        } catch (e) {
          console.log('Winners not available');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get CMS section by type
  const getSection = (type) => {
    return pageContent?.sections?.find(s => s.type === type && s.is_active !== false);
  };

  // Get CMS text or fallback to default
  const getText = (sectionType, field, defaultValue) => {
    const section = getSection(sectionType);
    return section?.[field] || defaultValue;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const iceCreamData = await getMenuItems('ice_cream');
        setIceCreams(iceCreamData.slice(0, 6));
        
        // Fetch current winners for display
        try {
          const winnersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners`);
          if (winnersRes.ok) {
            setWinners(await winnersRes.json());
          }
        } catch (e) {
          console.log('Winners not available');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Validation functions
  const containsHtml = (str) => {
    if (!str) return false;
    return /<[^>]*>/g.test(str);
  };

  const isFakePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return true;
    if (digits.includes('555')) return true;
    if (new Set(digits).size <= 2) return true;
    return false;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    // Honeypot check
    if (honeypot) return { valid: false, error: 'Spam detected' };
    
    // Too fast submission check
    if (Date.now() - formLoadTime < 3000) {
      return { valid: false, error: 'Please take your time filling out the form' };
    }

    // Required fields
    if (!drawingForm.name.trim()) return { valid: false, error: 'Name is required' };
    if (!isValidEmail(drawingForm.email)) return { valid: false, error: 'Valid email is required' };
    if (isFakePhone(drawingForm.phone)) return { valid: false, error: 'Valid phone number is required (no 555 numbers)' };
    if (!drawingForm.smsConsent) return { valid: false, error: 'SMS consent is required to enter the drawing' };

    // HTML check
    const fields = [drawingForm.name, drawingForm.email, drawingForm.phone, drawingForm.comment];
    for (const field of fields) {
      if (containsHtml(field)) return { valid: false, error: 'HTML is not allowed' };
    }

    // Vendor validations
    if (entryType === 'vendor') {
      if (!drawingForm.companyName.trim()) return { valid: false, error: 'Company name is required' };
      if (isFakePhone(drawingForm.companyPhone)) return { valid: false, error: 'Valid company phone is required' };
      if (!drawingForm.websiteUrl.trim()) return { valid: false, error: 'Website URL is required' };
      if (!drawingForm.position.trim()) return { valid: false, error: 'Position is required' };
    }

    return { valid: true };
  };

  const handleDrawingSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (!validation.valid) {
      toast({ title: 'Error', description: validation.error, variant: 'destructive' });
      return;
    }

    setFormSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: drawingForm.name,
          email: drawingForm.email,
          phone: drawingForm.phone,
          sms_consent: drawingForm.smsConsent,
          comment: drawingForm.comment || null,
          entry_type: entryType,
          company_name: entryType === 'vendor' ? drawingForm.companyName : null,
          company_phone: entryType === 'vendor' ? drawingForm.companyPhone : null,
          website_url: entryType === 'vendor' ? drawingForm.websiteUrl : null,
          position: entryType === 'vendor' ? drawingForm.position : null,
          best_contact_method: drawingForm.bestContactMethod
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit entry');
      }

      toast({ title: '🎉 You\'re Entered!', description: 'Good luck in the weekly drawing!' });
      trackEvent('drawing_entry_submitted', { entry_type: entryType, source: 'home_page' });
      setDrawingForm({ 
        name: '', email: '', phone: '', smsConsent: false, comment: '',
        companyName: '', companyPhone: '', websiteUrl: '', position: '', bestContactMethod: 'email'
      });
      setEntryType('customer');
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setFormSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBE8E8]">
        <div className="text-4xl font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Loading...</div>
      </div>
    );
  }

  // Ice cream flavors data - CMS or fallback
  const iceCreamFlavorsSection = getSection('ice_cream_flavors');
  const iceCreamFlavors = iceCreamFlavorsSection?.items?.length > 0 ? iceCreamFlavorsSection.items : [
    { name: 'Butter Pecan', image: '/images/icecream/butterpecan.webp' },
    { name: 'Chocolate Chip Cookie Dough', image: '/images/icecream/chocolatechipcookiedough.webp' },
    { name: 'Coffee Almond Fudge', image: '/images/icecream/coffeealmondfudge.webp' },
    { name: 'Cookies And Cream', image: '/images/icecream/cookiesandcream.webp' },
    { name: 'Mint Chocolate Chip', image: '/images/icecream/mintchocolatechip.webp' },
    { name: 'Mudslide', image: '/images/icecream/Mudslide.webp' },
  ];

  // Specialties data - CMS or fallback
  const specialtiesSection = getSection('specialties');
  const specialties = specialtiesSection?.items?.length > 0 ? specialtiesSection.items : [
    { name: 'Famous Ice Cream Stackers', image: '/images/specialties/stackers.png', famous: true },
    { name: 'Energy Drinks by Monin', image: '/images/specialties/energy-drinks.png', famous: false },
    { name: 'Cookies, Brownies, Tarts', image: '/images/specialties/cookies-brownies.png', famous: false },
    { name: 'Famous Ice Cream Nachos', image: '/images/specialties/nachos.png', famous: true },
    { name: 'Lattes, Espressos, Cappuccinos', image: '/images/specialties/coffee.png', famous: false },
    { name: 'Cakes, Pies', image: '/images/specialties/cakes.png', famous: false },
  ];

  // Features data - CMS or fallback
  const featuresSection = getSection('features');
  
  // Helper to resolve icon path - handles both full paths and icon names
  const resolveIconPath = (icon) => {
    if (!icon) return '/images/icons/star.svg';
    // If already a full path or URL, use as-is
    if (icon.startsWith('/') || icon.startsWith('http')) return icon;
    // Otherwise, assume it's an icon name and build the path
    return `/images/icons/${icon}.svg`;
  };
  
  const features = featuresSection?.items?.length > 0 
    ? featuresSection.items.map(item => ({
        ...item,
        icon: resolveIconPath(item.icon)
      }))
    : [
    { 
      title: 'Beautiful Venue', 
      description: 'Our 125 year-old building is located directly across from the historic Baker Hotel',
      icon: '/images/icons/red-carpet.svg'
    },
    { 
      title: 'Fun & Family-friendly', 
      description: 'Perfect for friends, date night, kids, family night-out & special occasions',
      icon: '/images/icons/balloons.svg'
    },
    { 
      title: 'Art Gallery & Music', 
      description: "Famous local artists' works of paintings, photography, glass works & crafts. Most for sale",
      icon: '/images/icons/catering.svg'
    },
    { 
      title: 'Tasty Treats', 
      description: 'Something for every taste and every budget. Choose from our specialties or build your own',
      icon: '/images/icons/wifi.svg'
    },
  ];

  // Pastries data - CMS or fallback
  const pastriesSection = getSection('pastries');
  const pastries = pastriesSection?.items?.length > 0 ? pastriesSection.items.map(p => p.image || p) : [
    '/images/pastries/Frame-1425.png',
    '/images/pastries/Frame-1426.png',
    '/images/pastries/Frame-1426-1.png',
    '/images/pastries/sdf.png',
    '/images/pastries/Frame-1425-1.png',
    '/images/pastries/Frame-1426-2.png',
    '/images/pastries/Frame-1426-4.png',
  ];

  // Hero section content - CMS or fallback
  const heroSection = getSection('hero');
  const heroTitle = heroSection?.title || 'The Best Place To Be In Mineral Wells, TX';
  const heroImage = heroSection?.image || '/images/hero/hero-cone.png';

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <SEO 
        title={pageContent?.page_title || "All-Natural Ice Cream & Pastries"}
        description={pageContent?.meta_description || "1st Ave ICP - The best ice cream shop in Mineral Wells, TX! All-natural ice cream made with the highest quality ingredients, specialty sundaes, ice cream stackers, fresh pastries, milkshakes & more. Visit us at 103 NE 1st Ave."}
        keywords={pageContent?.meta_keywords || "ice cream Mineral Wells TX, best ice cream shop Mineral Wells, all natural ice cream Texas, pastry shop Mineral Wells, dessert near me Palo Pinto County, ice cream stackers"}
        canonical="/"
        type="restaurant"
      />
      {/* Hero Section */}
      <section 
        className="relative bg-[#FBE8E8] pt-28 lg:pt-32 pb-8 lg:pb-0 min-h-[90vh] overflow-hidden"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center min-h-[70vh]">
            {/* Left - Heading */}
            <div className="text-center lg:text-left">
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight text-black"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {heroTitle}
              </h1>
            </div>
            
            {/* Right - Ice Cream Image */}
            <div className="flex justify-center lg:justify-end">
              <picture>
                {heroImage && localWebpSrc(heroImage) && <source srcSet={localWebpSrc(heroImage)} type="image/webp" />}
                <img
                  src={heroImage}
                  alt="Delicious ice cream cone at 1st Ave ICP Mineral Wells TX"
                  className="w-[150px] md:w-[200px] lg:w-[225px] xl:w-[250px] h-auto object-contain drop-shadow-2xl"
                  width="500"
                  height="600"
                  fetchPriority="high"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* When Life Hands You Ice Cream Section */}
      <section 
        className="bg-[#FBE8E8] py-16 lg:py-24 px-4 lg:px-8"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left - Polaroid Style Image Grid */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative w-full max-w-lg">
                {/* Large polaroid - Ice cream cones in jar */}
                <div className="bg-white p-3 pb-8 shadow-xl transform -rotate-3 relative z-10">
                  <picture>
                    {localWebpSrc(getText('polaroid', 'main_image', '/images/misc/polaroid-main.jpg')) && <source srcSet={localWebpSrc(getText('polaroid', 'main_image', '/images/misc/polaroid-main.jpg'))} type="image/webp" />}
                    <img
                      src={getText('polaroid', 'main_image', '/images/misc/polaroid-main.jpg')}
                      alt="Child enjoying an ice cream treat with waffle crisps at 1st Ave ICP"
                      className="w-full h-[350px] lg:h-[450px] object-cover"
                      loading="lazy"
                    />
                  </picture>
                </div>
                {/* Small polaroid - top right */}
                <div className="absolute -top-4 -right-4 lg:-right-16 bg-white p-2 pb-6 shadow-xl transform rotate-6 z-20 w-40 lg:w-48">
                  <picture>
                    {localWebpSrc(getText('polaroid', 'small_image_1', '/images/misc/polaroid-small1.jpg')) && <source srcSet={localWebpSrc(getText('polaroid', 'small_image_1', '/images/misc/polaroid-small1.jpg'))} type="image/webp" />}
                    <img
                      src={getText('polaroid', 'small_image_1', '/images/misc/polaroid-small1.jpg')}
                      alt="Family enjoying ice cream at 1st Ave ICP"
                      className="w-full h-32 lg:h-40 object-cover"
                      loading="lazy"
                    />
                  </picture>
                </div>
                {/* Small polaroid - bottom right */}
                <div className="absolute -bottom-8 -right-2 lg:-right-12 bg-white p-2 pb-6 shadow-xl transform rotate-3 z-20 w-36 lg:w-44">
                  <picture>
                    {localWebpSrc(getText('polaroid', 'small_image_2', '/images/misc/polaroid-small2.png')) && <source srcSet={localWebpSrc(getText('polaroid', 'small_image_2', '/images/misc/polaroid-small2.png'))} type="image/webp" />}
                    <img
                      src={getText('polaroid', 'small_image_2', '/images/misc/polaroid-small2.png')}
                      alt="Happy customer holding a strawberry sundae outside 1st Ave ICP"
                      className="w-full h-40 lg:h-48 object-cover"
                      loading="lazy"
                    />
                  </picture>
                </div>
              </div>
            </div>
            
            {/* Right - Content */}
            <div className="lg:pl-8">
              <div className="flex items-center gap-4 mb-8">
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-black"
                  style={{ fontFamily: 'Rowdies, cursive' }}
                >
                  {getText('tagline', 'title', 'When Life Hands You Ice Cream, Eat It!')}
                </h2>
                <OptimizedImage
                  src="/images/icons/logo-small.png"
                  alt="1st Ave ICP Logo"
                  className="w-16 h-16 flex-shrink-0"
                  width={64}
                  height={64}
                />
              </div>
              
              <div className="space-y-4">
                {/* Nachos Card */}
                <div className="flex items-center gap-4">
                  <OptimizedImage
                    src="/images/specialties/nachos.png"
                    alt="Ice Cream Nachos - specialty at 1st Ave ICP Mineral Wells"
                    className="w-20 h-20 object-contain"
                    width={80}
                    height={80}
                  />
                  <span className="text-xl font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Nachos</span>
                </div>
                
                {/* Stackers Card */}
                <div className="flex items-center gap-4">
                  <OptimizedImage
                    src="/images/specialties/stackers.png"
                    alt="Stackers"
                    className="w-20 h-20 object-contain"
                    width={80}
                    height={80}
                  />
                  <span className="text-xl font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Stackers</span>
                </div>
                
                {/* Ice Cream Treats Card */}
                <div className="flex items-center gap-4">
                  <OptimizedImage
                    src="/images/specialties/treats.png"
                    alt="Ice Cream Treats"
                    className="w-20 h-20 object-contain"
                    width={80}
                    height={80}
                  />
                  <span className="text-xl font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Ice Cream Treats</span>
                </div>
                
                <Link to="/menu" className="inline-block mt-6">
                  <Button 
                    className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold text-lg px-10 py-6 rounded-full shadow-lg uppercase tracking-wide"
                  >
                    MENU
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A Taste Worth Remembering Section */}
      <section className="bg-[#4A9A8D] py-20 lg:py-28 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
            {/* Left Polaroid - Brownies Stack */}
            <div className="hidden lg:block transform -rotate-12">
              <div className="bg-white p-2 pb-10 shadow-2xl" style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                <div className="bg-[#4A9A8D] w-48 h-52 flex items-center justify-center">
                  <OptimizedImage
                    src={getText('taste_section', 'left_image', '/images/misc/brownie-stack.png')}
                    alt="Brownies Stack"
                    className="w-full h-full object-contain"
                    width={192}
                    height={208}
                  />
                </div>
              </div>
            </div>
            
            {/* Center Title */}
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {getText('taste_section', 'title', 'A Taste Worth Remembering').split(' ').slice(0, 3).join(' ')}<br/>{getText('taste_section', 'title', 'A Taste Worth Remembering').split(' ').slice(3).join(' ') || 'Remembering'}
            </h2>
            
            {/* Right Polaroid - Cookie */}
            <div className="hidden lg:block transform rotate-12">
              <div className="bg-white p-2 pb-10 shadow-2xl" style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                <div className="bg-[#4A9A8D] w-48 h-52 flex items-center justify-center">
                  <OptimizedImage
                    src={getText('taste_section', 'right_image', '/images/misc/cookie-polaroid.png')}
                    alt="Cookie"
                    className="w-full h-full object-contain"
                    width={192}
                    height={208}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ice Cream Selection Section */}
      <section className="bg-[#4A9A8D] py-8 lg:py-16 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* White Card Container with Gradient Border */}
          <div 
            className="rounded-lg p-[6px] shadow-2xl relative"
            style={{
              background: 'linear-gradient(135deg, #E91E8C 0%, #C71585 25%, #9B1B6E 50%, #7B1454 75%, #5A0F3D 100%)'
            }}
          >
            <div className="bg-white rounded-lg p-8 lg:p-12">
              {/* Logo at top */}
              <div className="flex justify-center -mt-16 lg:-mt-20 mb-6">
                <OptimizedImage
                  src="/images/logo/logo-circle.png"
                  alt="1st Ave ICP Logo"
                  className="w-20 h-20 lg:w-28 lg:h-28 rounded-full shadow-lg bg-white"
                  width={112}
                  height={112}
                  priority={true}
                />
              </div>
              
              {/* Heading */}
              <div className="text-center mb-8">
                <h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-4"
                  style={{ fontFamily: 'Rowdies, cursive' }}
                >
                  {getText('ice_cream_section', 'title', 'All Natural Ice Cream Selection')}
                </h2>
                <p 
                  className="text-lg md:text-xl font-bold text-black"
                  style={{ fontFamily: 'Rowdies, cursive' }}
                >
                  {getText('ice_cream_section', 'subtitle', 'No Artificial Colors, Flavors, Preservatives or High Fructose Corn Syrup')}
                </p>
              </div>
              
              {/* Menu Button */}
              <div className="flex justify-center mb-10">
                <Link to="/menu">
                  <Button 
                    className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-10 py-5 rounded-full shadow-lg"
                  >
                    Menu
                  </Button>
                </Link>
              </div>
              
              {/* Ice Cream Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8 lg:gap-10 px-2">
                {iceCreamFlavors.map((flavor, index) => (
                  <div key={index} className="text-center">
                  <div className="w-full aspect-square rounded-full overflow-hidden mb-4 shadow-md mx-auto max-w-[140px] md:max-w-[180px]">
                    <OptimizedImage
                      src={flavor.image}
                      alt={flavor.name}
                      className="w-full h-full object-cover"
                      width={180}
                      height={180}
                    />
                  </div>
                  <h3 
                    className="text-base md:text-xl font-bold text-black px-1"
                    style={{ fontFamily: 'Rowdies, cursive' }}
                  >
                    {flavor.name}
                  </h3>
                </div>
              ))}
            </div>
            
            {/* View Menu Button */}
            <div className="flex justify-center mt-10">
              <Link to="/menu" aria-label="View our full ice cream and pastry menu">
                <Button 
                  className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-10 py-5 rounded-full shadow-lg"
                >
                  View Full Menu
                </Button>
              </Link>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Specialties Section */}
      <section 
        className="bg-[#FBE8E8] py-16 lg:py-24 px-4 lg:px-8"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-black text-center mb-12 lg:mb-16"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            {getText('specialties', 'title', 'Our Specialties')}
          </h2>
          
          {/* Mobile: 2-column grid without ice cream cone */}
          <div className="grid grid-cols-2 gap-6 lg:hidden">
            {specialties.map((item, index) => (
              <div key={index} className="text-center">
                <div className="flex flex-col items-center">
                  <OptimizedImage
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 sm:w-32 sm:h-32 object-contain mb-2"
                    width={128}
                    height={128}
                  />
                  <div>
                    {item.famous && (
                      <div className="flex items-center justify-center gap-1 text-xs mb-1">
                        <OptimizedImage src="/images/icons/star.png" alt="star" className="w-3 h-3" width={12} height={12} lazy={false} />
                        <span className="text-black font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Famous</span>
                        <OptimizedImage src="/images/icons/star.png" alt="star" className="w-3 h-3" width={12} height={12} lazy={false} />
                      </div>
                    )}
                    <h3 
                      className="text-sm sm:text-base font-bold text-black leading-tight"
                      style={{ fontFamily: 'Rowdies, cursive' }}
                    >
                      {item.famous ? <>Ice Cream<br/>{item.name.replace('Famous Ice Cream ', '')}</> : item.name}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Three Column Layout with Center Ice Cream */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-8 items-center">
            {/* Left Column */}
            <div className="space-y-8">
              {specialties.slice(0, 3).map((item, index) => (
                <div key={index} className="text-center">
                  <div className="flex flex-col items-center">
                    <OptimizedImage
                      src={item.image}
                      alt={item.name}
                      className="w-48 h-48 object-contain mb-3"
                      width={192}
                      height={192}
                    />
                    <div>
                      {item.famous && (
                        <div className="flex items-center justify-center gap-1 text-sm mb-1">
                          <OptimizedImage src="/images/icons/star.png" alt="star" className="w-4 h-4" width={16} height={16} lazy={false} />
                          <span className="text-black font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Famous</span>
                          <OptimizedImage src="/images/icons/star.png" alt="star" className="w-4 h-4" width={16} height={16} lazy={false} />
                        </div>
                      )}
                      <h3 
                        className="text-lg font-bold text-black leading-tight"
                        style={{ fontFamily: 'Rowdies, cursive' }}
                      >
                        {item.famous ? <>Ice Cream<br/>{item.name.replace('Famous Ice Cream ', '')}</> : item.name}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Center - Pointed Dripping Ice Cream Cone */}
            <div className="flex justify-center py-0 relative">
              <div className="relative">
                <OptimizedImage
                  src="/images/hero/hero-cone.png"
                  alt="Colorful Dripping Ice Cream Cone"
                  className="w-96 xl:w-[450px] h-auto object-contain drop-shadow-2xl"
                  width={450}
                  height={600}
                />
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-8">
              {specialties.slice(3, 6).map((item, index) => (
                <div key={index} className="text-center">
                  <div className="flex flex-col items-center">
                    <OptimizedImage
                      src={item.image}
                      alt={item.name}
                      className="w-48 h-48 object-contain mb-3"
                      width={192}
                      height={192}
                    />
                    <div>
                      {item.famous && (
                        <div className="flex items-center justify-center gap-1 text-sm mb-1">
                          <OptimizedImage src="/images/icons/star.png" alt="star" className="w-4 h-4" width={16} height={16} lazy={false} />
                          <span className="text-black font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>Famous</span>
                          <OptimizedImage src="/images/icons/star.png" alt="star" className="w-4 h-4" width={16} height={16} lazy={false} />
                        </div>
                      )}
                      <h3 
                        className="text-lg font-bold text-black leading-tight"
                        style={{ fontFamily: 'Rowdies, cursive' }}
                      >
                        {item.famous ? <>Ice Cream<br/>{item.name.replace('Famous Ice Cream ', '')}</> : item.name}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Circus Mural Banner with Cloud Edges - ABOVE Come See Us */}
      <section 
        className="relative overflow-hidden"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        {/* Circus Mural with smooth wavy overlays */}
        <div className="w-full relative">
          {/* Top smooth wavy overlay */}
          <svg 
            className="absolute top-0 left-0 w-full z-10 pointer-events-none"
            viewBox="0 0 1440 100" 
            preserveAspectRatio="none"
            style={{ height: '80px' }}
          >
            <defs>
              <pattern id="waveBgTop" patternUnits="userSpaceOnUse" width="600" height="600">
                <image href="/images/misc/storefront-bg.jpg" width="600" height="600" preserveAspectRatio="xMidYMid slice" />
              </pattern>
            </defs>
            <path 
              d="M0,0 L0,60 Q60,80 120,55 Q180,30 240,50 Q300,70 360,45 Q420,20 480,40 Q540,60 600,35 Q660,10 720,30 Q780,50 840,25 Q900,0 960,25 Q1020,50 1080,30 Q1140,10 1200,35 Q1260,60 1320,40 Q1380,20 1440,45 L1440,0 Z" 
              fill="url(#waveBgTop)"
            />
          </svg>
          
          {/* Circus Mural Image - cropped height */}
          <div className="w-full h-[310px] sm:h-[400px] md:h-[500px] lg:h-[580px] overflow-hidden">
            <img 
              src={getText('mural', 'image', '/images/misc/circus-mural.png')}
              alt="Circus Mural"
              className="w-full h-full object-cover object-center"
              loading="lazy"
            />
          </div>
          
          {/* Bottom smooth wavy overlay */}
          <svg 
            className="absolute bottom-0 left-0 w-full z-10 pointer-events-none"
            viewBox="0 0 1440 100" 
            preserveAspectRatio="none"
            style={{ height: '80px' }}
          >
            <defs>
              <pattern id="waveBgBottom" patternUnits="userSpaceOnUse" width="600" height="600">
                <image href="/images/misc/storefront-bg.jpg" width="600" height="600" preserveAspectRatio="xMidYMid slice" />
              </pattern>
            </defs>
            <path 
              d="M0,100 L0,40 Q60,20 120,45 Q180,70 240,50 Q300,30 360,55 Q420,80 480,60 Q540,40 600,65 Q660,90 720,70 Q780,50 840,75 Q900,100 960,75 Q1020,50 1080,70 Q1140,90 1200,65 Q1260,40 1320,60 Q1380,80 1440,55 L1440,100 Z" 
              fill="url(#waveBgBottom)"
            />
          </svg>
        </div>
      </section>

      {/* Come See Us Section - Plain pink wallpaper */}
      <section 
        className="py-12 lg:py-16 px-4 lg:px-8"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left - Horizontal Oval Frame Image - Store Staff */}
            <div className="flex justify-center">
              <div 
                className="w-72 h-48 md:w-80 md:h-56 lg:w-96 lg:h-64 overflow-hidden shadow-xl"
                style={{
                  borderRadius: '50%',
                  border: '6px solid #F5A9B8'
                }}
              >
                <OptimizedImage
                  src={getText('features', 'image', '/images/misc/COMESEEUS.jpg')}
                  alt="Staff at 1st Ave ICP"
                  className="w-full h-full object-cover object-center"
                  width={384}
                  height={256}
                />
              </div>
            </div>
          
            {/* Right - Content */}
            <div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-black mb-6 text-center lg:text-left"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {getText('features', 'title', 'Come See Us!')}
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <OptimizedImage
                      src={feature.icon}
                      alt={feature.title}
                      className="w-8 h-8 mx-auto lg:mx-0 mb-1"
                      width={32}
                      height={32}
                    />
                    <h3 
                      className="text-sm font-bold text-black mb-1"
                      style={{ fontFamily: 'Rowdies, cursive' }}
                    >
                      {feature.title}
                    </h3>
                    <p className="text-xs text-gray-700 leading-tight">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pastries Section */}
      <section className="bg-[#4A9A8D] py-16 lg:py-24 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Double Border Frame - Pink outer, Gradient inner */}
          <div 
            className="rounded-lg p-[8px]"
            style={{
              background: 'linear-gradient(135deg, #E91E8C 0%, #C71585 50%, #9B1B6E 100%)'
            }}
          >
            <div className="bg-white rounded-lg p-6 lg:p-10">
              <h2 
                className="text-3xl md:text-4xl font-bold text-black text-center mb-8"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {getText('pastries', 'title', 'Pastries')}
              </h2>
              
              {/* Pastries Grid - Responsive layout */}
              {/* Mobile: 2 columns, Desktop: 3 columns with center tall */}
              <div className="hidden md:grid grid-cols-3 gap-3 lg:gap-4">
                {/* Left column - Chocolate cake */}
                <div className="space-y-3 lg:space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[180px] lg:h-[220px]">
                    <OptimizedImage
                      src="/images/pastries/Frame-1425.png"
                      alt="Chocolate Cake Slice"
                      className="w-full h-full object-cover"
                      width={300}
                      height={220}
                    />
                  </div>
                  {/* Bottom left - 2 small square images side by side */}
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[100px] lg:h-[120px]">
                      <OptimizedImage
                        src="/images/pastries/Frame-1434-1.png"
                        alt="Layered Vanilla Cake"
                        className="w-full h-full object-cover"
                        width={150}
                        height={120}
                      />
                    </div>
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[100px] lg:h-[120px]">
                      <OptimizedImage
                        src="/images/pastries/Frame-1426-1.png"
                        alt="Layered Mousse Tart"
                        className="w-full h-full object-cover"
                        width={150}
                        height={120}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Center column - Tall brownie stack */}
                <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[292px] lg:h-[356px]">
                  <OptimizedImage
                    src="/images/pastries/sdf.png"
                    alt="Brownie Stack"
                    className="w-full h-full object-cover"
                    width={300}
                    height={356}
                  />
                </div>
                
                {/* Right column - Cookies */}
                <div className="space-y-3 lg:space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[180px] lg:h-[220px]">
                    <OptimizedImage
                      src="/images/pastries/cookie-stack.png"
                      alt="Colorful Cookie Stack"
                      className="w-full h-full object-cover"
                      width={300}
                      height={220}
                    />
                  </div>
                  {/* Bottom right - 2 small square images side by side */}
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[100px] lg:h-[120px]">
                      <OptimizedImage
                        src="/images/pastries/Frame-1426.png"
                        alt="Chocolate Brownie Cake"
                        className="w-full h-full object-cover"
                        width={150}
                        height={120}
                      />
                    </div>
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-[100px] lg:h-[120px]">
                      <OptimizedImage
                        src="/images/pastries/Frame-1426-4.png"
                        alt="Cookie with Coffee"
                        className="w-full h-full object-cover"
                        width={150}
                        height={120}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Mobile Layout: 2x3 grid - shorter heights, wider */}
              <div className="grid md:hidden grid-cols-2 gap-2">
                <div className="rounded-xl overflow-hidden shadow-md h-[100px]">
                  <OptimizedImage src="/images/pastries/Frame-1425.png" alt="Chocolate Cake" className="w-full h-full object-cover" width={200} height={100} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md h-[100px]">
                  <OptimizedImage src="/images/pastries/cookie-stack.png" alt="Cookie Stack" className="w-full h-full object-cover" width={200} height={100} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md col-span-2 h-[120px]">
                  <OptimizedImage src="/images/pastries/sdf.png" alt="Brownie Stack" className="w-full h-full object-cover" width={400} height={120} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md h-[80px]">
                  <OptimizedImage src="/images/pastries/Frame-1426-2.png" alt="Layered Cake" className="w-full h-full object-cover" width={200} height={80} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md h-[80px]">
                  <OptimizedImage src="/images/pastries/Frame-1426-1.png" alt="Mousse Tart" className="w-full h-full object-cover" width={200} height={80} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md h-[80px]">
                  <OptimizedImage src="/images/pastries/Frame-1426.png" alt="Brownie Cake" className="w-full h-full object-cover" width={200} height={80} />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md h-[80px]">
                  <OptimizedImage src="/images/pastries/Frame-1426-4.png" alt="Cookie with Coffee" className="w-full h-full object-cover" width={200} height={80} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Drawing Section */}
      <section 
        className="bg-[#FBE8E8] py-16 lg:py-24 px-4 lg:px-8"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Winners Display - Show when there's a current winner (with photo or placeholder) */}
          {winners.current_winner && (
            <div className="mb-12">
              <div className="text-center mb-8">
                <span className="inline-block bg-[#E91E8C] text-white px-4 py-1 rounded-full text-sm font-bold mb-3">
                  🏆 WEEKLY DRAWING WINNER
                </span>
                <h2 
                  className="text-3xl md:text-4xl font-bold"
                  style={{ fontFamily: 'Rowdies, cursive' }}
                >
                  Congratulations!
                </h2>
              </div>
              
              <div className="flex flex-wrap justify-center gap-6">
                {/* Current Winner - With photo or placeholder */}
                <div className="bg-gradient-to-br from-pink-100 to-pink-50 p-6 rounded-2xl border-4 border-[#E91E8C] shadow-xl max-w-sm text-center">
                  <div className="text-4xl mb-2">🎉</div>
                  <div className="bg-[#E91E8C] text-white px-3 py-1 rounded-full text-xs font-bold inline-block mb-3">
                    THIS WEEK&apos;S WINNER
                  </div>
                  {winners.current_winner.image_url ? (
                    <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-[#E91E8C] mb-3">
                      <img 
                        src={winners.current_winner.image_url} 
                        alt={winners.current_winner.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-pink-200 to-purple-200 border-4 border-[#E91E8C] mb-3 flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-2xl">📸</span>
                        <p className="text-xs text-gray-500 font-medium">Photo Coming Soon</p>
                      </div>
                    </div>
                  )}
                  <p className="text-2xl font-black text-[#E91E8C]">{winners.current_winner.name}</p>
                  {winners.current_winner.prize && (
                    <p className="text-gray-600 mt-1">Won: {winners.current_winner.prize}</p>
                  )}
                  {/* Week Date */}
                  <p className="text-xs text-gray-400 mt-2">
                    {(() => {
                      const date = winners.current_winner.win_date ? new Date(winners.current_winner.win_date) : (winners.current_winner.created_at ? new Date(winners.current_winner.created_at) : new Date());
                      const weekStart = new Date(date);
                      weekStart.setDate(date.getDate() - date.getDay());
                      const weekEnd = new Date(weekStart);
                      weekEnd.setDate(weekStart.getDate() + 6);
                      const month = weekStart.toLocaleString('default', { month: 'long' });
                      return `Week of ${month} ${weekStart.getDate()}-${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
                    })()}
                  </p>
                </div>
                
                {/* Past Winners - Show all, with photo or placeholder */}
                {winners.past_winners?.length > 0 && (
                  <div className="bg-white/90 p-5 rounded-2xl shadow-lg max-w-sm">
                    <h3 className="text-sm font-bold text-gray-500 mb-3 text-center">Previous Winners</h3>
                    <div className="space-y-2">
                      {winners.past_winners.slice(0, 4).map((winner, idx) => (
                        <div key={winner.id || idx} className="flex items-center gap-3 p-2 bg-pink-50 rounded-lg">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-pink-100 flex-shrink-0 flex items-center justify-center">
                            {winner.image_url ? (
                              <img src={winner.image_url} alt={winner.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-lg">📸</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-sm">{winner.name}</p>
                            {winner.prize && <p className="text-xs text-gray-500">{winner.prize}</p>}
                            <p className="text-xs text-gray-400">
                              {(() => {
                                const date = winner.win_date ? new Date(winner.win_date) : (winner.created_at ? new Date(winner.created_at) : new Date());
                                const weekStart = new Date(date);
                                weekStart.setDate(date.getDate() - date.getDay());
                                const weekEnd = new Date(weekStart);
                                weekEnd.setDate(weekStart.getDate() + 6);
                                return `Week of ${weekStart.toLocaleString('default', { month: 'short' })} ${weekStart.getDate()}-${weekEnd.getDate()}`;
                              })()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Link 
                      to="/winners" 
                      className="block text-center mt-4 text-[#E91E8C] font-bold text-sm hover:underline"
                    >
                      View All Winners →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Weekly Drawing Form */}
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-black mb-2"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {getText('drawing', 'title', '🎉 Enter Our Weekly Drawing!')}
              </h2>
              <p className="text-gray-600 mb-6">{getText('drawing', 'subtitle', 'Win free ice cream & exclusive offers!')}</p>
              
              <form onSubmit={handleDrawingSubmit} className="space-y-4">
                {/* Honeypot - hidden from users */}
                <input
                  type="text"
                  name="website_confirm"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                {/* Customer/Vendor Selection */}
                <div className="bg-pink-50 p-3 rounded-lg">
                  <label className="block text-sm font-bold text-gray-700 mb-2">I am a:</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="entryType"
                        value="customer"
                        checked={entryType === 'customer'}
                        onChange={(e) => setEntryType(e.target.value)}
                        className="w-4 h-4 accent-pink-500"
                      />
                      <span className="font-medium">Customer</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="entryType"
                        value="vendor"
                        checked={entryType === 'vendor'}
                        onChange={(e) => setEntryType(e.target.value)}
                        className="w-4 h-4 accent-pink-500"
                      />
                      <span className="font-medium">Vendor</span>
                    </label>
                  </div>
                </div>

                {/* Basic Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Name *"
                    value={drawingForm.name}
                    onChange={(e) => setDrawingForm({...drawingForm, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={drawingForm.email}
                    onChange={(e) => setDrawingForm({...drawingForm, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    required
                  />
                </div>
                
                <input
                  type="tel"
                  placeholder="Phone Number * (for text notifications)"
                  value={drawingForm.phone}
                  onChange={(e) => setDrawingForm({...drawingForm, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  required
                />

                {/* Vendor Fields */}
                {entryType === 'vendor' && (
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-bold text-gray-700">Vendor Information</p>
                    <input
                      type="text"
                      placeholder="Company Name *"
                      value={drawingForm.companyName}
                      onChange={(e) => setDrawingForm({...drawingForm, companyName: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    />
                    <input
                      type="tel"
                      placeholder="Company Phone *"
                      value={drawingForm.companyPhone}
                      onChange={(e) => setDrawingForm({...drawingForm, companyPhone: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Website URL *"
                      value={drawingForm.websiteUrl}
                      onChange={(e) => setDrawingForm({...drawingForm, websiteUrl: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Your Position *"
                      value={drawingForm.position}
                      onChange={(e) => setDrawingForm({...drawingForm, position: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    />
                    <select
                      value={drawingForm.bestContactMethod}
                      onChange={(e) => setDrawingForm({...drawingForm, bestContactMethod: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                    >
                      <option value="email">Best Contact: Email</option>
                      <option value="phone">Best Contact: Phone</option>
                      <option value="text">Best Contact: Text</option>
                    </select>
                  </div>
                )}

                <textarea
                  placeholder="Comment (optional)"
                  rows="3"
                  value={drawingForm.comment}
                  onChange={(e) => setDrawingForm({...drawingForm, comment: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none resize-none"
                ></textarea>

                {/* SMS Consent - Required */}
                <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={drawingForm.smsConsent}
                      onChange={(e) => setDrawingForm({...drawingForm, smsConsent: e.target.checked})}
                      className="w-5 h-5 mt-0.5 accent-teal-500"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      <strong className="text-teal-700">Required:</strong> I agree to receive text messages from 1st Ave ICP about the weekly drawing, promotions, and special offers. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe.
                    </span>
                  </label>
                </div>

                <Button 
                  type="submit"
                  disabled={formSubmitting || !drawingForm.smsConsent}
                  className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-12 py-4 rounded-full shadow-lg w-full disabled:opacity-50"
                >
                  {formSubmitting ? 'Entering...' : '🎰 Enter Drawing'}
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  One entry per person. Winners notified via email & text.
                </p>
              </form>
            </div>
            
            {/* Right - Cookie/Brownie Stack Image */}
            <div className="flex justify-center">
              <OptimizedImage
                src={getText('drawing', 'image', '/images/misc/1000202320.png')}
                alt="Delicious Cookie Stack"
                className="w-64 md:w-80 lg:w-96 h-auto object-contain drop-shadow-lg"
                width={384}
                height={384}
              />
            </div>
          </div>
        </div>
      </section>
      <ReviewNudge />
    </div>
  );
};

export default Home;
